import { TopicKey } from "@hub/data/values/data-keys.enum";
import { LabCardData } from "@hub/models/document/document-card-data";
import { LabFilterGroups } from "@hub/models/filters/code-sample-filter-groups";
import { HeadMetadata } from "@hub/models/metadata/head-metadata";
import { BannerConfig } from "@hub/models/ui/banner-config";
import { Breadcrumb } from "@hub/models/ui/breadcrumb";
import { NavigationFolder } from "@hub/models/ui/navigation-folder";
import { NavigationFolders } from "@hub/models/ui/navigation-folders";
import { PageConfig } from "@hub/models/ui/page-config";
import {
  hubOriginUrl,
  hubSiteTree,
  SiteTreeSectionKeys,
} from "@hub/services/config.service";
import { getBreadCrumbs } from "@hub/services/content/common/get-breadcrumbs";
import { LabData } from "@hub/services/content/models/lab-data";
import { LabFilteringService } from "@hub/services/filtering/lab-filtering.service";
import { MetadataService } from "@hub/services/metadata.service";
import { createUrlPath } from "@hub/services/utils/path.utils";
import startCase from "lodash/startCase";
import { GetStaticProps } from "next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { AllLabsPage } from "../../components/all-labs-page";

interface LabsIndexProps {
  canonicalUrl: string;
  contentCardData: LabCardData[];
  filterOptions: LabFilterGroups;
  navigationFolders?: NavigationFolders;
  breadcrumbs?: Breadcrumb[];
}

const LabsIndex: React.FC<LabsIndexProps> = ({
  navigationFolders,
  canonicalUrl,
  contentCardData,
  filterOptions,
  breadcrumbs,
}) => {
  const { isReady, query } = useRouter();

  const filterQuery = query as {
    topic: string;
    searchTerm: string;
  };

  useEffect(() => {
    if (isReady && contentCardData) {
      const enabledFilters: {
        topic: boolean;
      } = {
        topic: true,
      };

      LabFilteringService.setEnabledFilterGroups(enabledFilters);
      LabFilteringService.setFilterOptions(filterOptions);
      LabFilteringService.initializeFilterGroups({
        query: filterQuery,
        contentCardData,
      });
    }
  }, [filterQuery, isReady, contentCardData]);

  const headMetadata: HeadMetadata = {
    title: "Auth0 Developer Labs",
    description:
      "Follow hands-on exercises to learn how to implement authentication and authorization using the Auth0 Identity Platform.",
    url: canonicalUrl,
    image:
      "https://cdn.auth0.com/website/developer-center/preview-cards/resources_labs.png",
    type: "website",
  };

  const pageConfig: PageConfig = {
    canonicalUrl,
    title: "Auth0 Developer Labs",
    description:
      "Follow hands-on exercises to learn how to implement authentication and authorization using the Auth0 Identity Platform.",
  };

  const bannerConfig: BannerConfig = {
    title: "Developer Labs",
    description:
      "Hands-on practice to learn about the identity and security features of Auth0.",
    withBorder: true,
  };

  return (
    <AllLabsPage
      bannerConfig={bannerConfig}
      pageConfig={pageConfig}
      navigationFolders={navigationFolders}
      breadcrumbs={breadcrumbs}
      headMetadata={headMetadata}
    />
  );
};

export default LabsIndex;

export const getStaticProps: GetStaticProps<LabsIndexProps> = async () => {
  const labData = new LabData();

  const navigationFolders: NavigationFolder[] = labData.allowed.topics
    .map((topic) => topic as TopicKey)
    .map((topic) => {
      const folder: {
        label: string;
        path: string;
      } = {
        label: MetadataService.getTopicDisplayName(topic) || startCase(topic),
        path: createUrlPath([
          hubSiteTree.root.urlPath,
          hubSiteTree.root.sections.labs.urlPath,
          topic,
        ]),
      };

      return folder;
    });

  const breadcrumbs = getBreadCrumbs({
    root: {
      label: hubSiteTree.root.displayName,
      path: hubSiteTree.root.urlPath,
    },
    sections: [
      {
        label: hubSiteTree.root.sections.labs.displayName,
        path: hubSiteTree.root.sections.labs.urlPath,
      },
    ],
  });

  const canonicalUrl =
    hubOriginUrl +
    createUrlPath([
      hubSiteTree.root.urlPath,
      hubSiteTree.root.sections[SiteTreeSectionKeys.LABS].urlPath,
    ]);

  const props: LabsIndexProps = {
    canonicalUrl,
    contentCardData: labData.cards.labs,
    filterOptions: labData.filters,
    navigationFolders: {
      group: "Topic",
      folders: navigationFolders,
      activePath: "",
    },
    breadcrumbs,
  };

  return {
    props,
  };
};
